// src/components/trial/lp/trial-beta/sections/faq.tsx
// Ported from the design attachment, markup lines 4046-4093. The source drives the accordion
// body's `grid-rows-[0fr]` -> `grid-rows-[1fr]` transition from vanilla JS toggling that class
// pair on click; reimplemented here as React state over the same class names — both Tailwind's
// own generated `.grid-rows-[Xfr]` / `.rotate-180` utilities and trial-beta.css's
// `[data-anim='faq-item'] > button[aria-expanded=...] + .grid` rules key off exactly this shape —
// plus `aria-expanded` / `aria-controls`, which the static markup didn't need to carry itself.
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { useId, useState } from 'react';

const FAQ_ITEM_COUNT = 6;

export function Faq() {
  const { t } = useTranslation('components/trial/lp/trial-beta');
  const baseId = useId();
  const [openItems, setOpenItems] = useState<boolean[]>(() => Array(FAQ_ITEM_COUNT).fill(false));

  const toggleItem = (toggledIndex: number) => {
    setOpenItems((previousOpenItems) => previousOpenItems.map((isOpen, itemIndex) => (itemIndex === toggledIndex ? !isOpen : isOpen)));
  };

  return (
    <section id='faq' className='relative px-4 py-14 md:px-8 md:py-20' style={{ zIndex: 1 }}>
      <div className='np-faq-grid' aria-hidden='true' />
      <div className='relative mx-auto max-w-7xl' style={{ zIndex: 1 }}>
        <div className='np-faq-layout'>
          <div className='np-faq-intro'>
            <div className='np-faq-heading mb-10 text-center md:mb-14'>
              <span className='mb-3 block text-xs font-semibold uppercase tracking-[0.3em] md:text-sm' style={{ color: '#2ECDFF', fontFamily: 'var(--font-outfit)' }}>
                {t('faq.eyebrow')}
              </span>
              <h2 className='mb-4 text-3xl font-bold md:text-5xl' style={{ fontFamily: 'var(--font-bai)', color: '#E5E7EB' }}>
                {t('faq.title')} <span>{t('faq.titleHighlight')}</span>
              </h2>
              <p>{t('faq.description')}</p>
            </div>
            <Link className='np-faq-support' href='/support'>
              <span className='np-faq-support-icon' aria-hidden='true'>
                <svg viewBox='0 0 32 32' fill='none'>
                  <path d='M7 15.5a9 9 0 0 1 18 0v6.2a3.3 3.3 0 0 1-3.3 3.3H19' />
                  <path d='M7 16H5.8A2.8 2.8 0 0 0 3 18.8v2.4A2.8 2.8 0 0 0 5.8 24H7V16Zm18 0h1.2a2.8 2.8 0 0 1 2.8 2.8v2.4a2.8 2.8 0 0 1-2.8 2.8H25V16Z' />
                  <rect x='14' y='23' width='6' height='4' rx='2' />
                </svg>
              </span>
              <span className='np-faq-support-copy'>
                <small>{t('faq.support.label')}</small>
                <strong>{t('faq.support.title')}</strong>
                <em>{t('faq.support.cta')}</em>
              </span>
              <span className='np-faq-support-arrow' aria-hidden='true'>
                ↗
              </span>
              <span className='np-faq-support-status'>
                <i aria-hidden='true' /> {t('faq.support.status')}
              </span>
            </Link>
          </div>
          <div className='np-faq-list space-y-3'>
            {openItems.map((isOpen, itemIndex) => {
              const panelId = `${baseId}-faq-panel-${itemIndex}`;
              return (
                <div key={panelId} data-anim='faq-item' className='overflow-hidden rounded-xl border border-white/[0.06] bg-[#12141a]'>
                  <button type='button' aria-expanded={isOpen} aria-controls={panelId} onClick={() => toggleItem(itemIndex)} className='flex w-full items-center justify-between px-6 py-5 text-start text-base font-semibold text-white transition-colors'>
                    <span className='np-faq-index'>{String(itemIndex + 1).padStart(2, '0')}</span>
                    <span className='np-faq-question'>{t(`faq.items.${itemIndex}.question`)}</span>
                    <svg className={`ms-2 size-5 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} style={{ color: '#93FF18' }} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={2} strokeLinecap='round' strokeLinejoin='round'>
                      <path d='m6 9 6 6 6-6' />
                    </svg>
                  </button>
                  <div id={panelId} className={`grid transition-all duration-200 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                    <div className='overflow-hidden'>
                      <p className='px-6 pb-5 leading-relaxed text-[#929298]'>{t(`faq.items.${itemIndex}.answer`)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
